import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { FiMail, FiCheck, FiLoader, FiArrowRight, FiRefreshCw, FiClock, FiArrowLeft, FiUser, FiEdit3 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function EmailConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp } = useAuth();

  const userEmail = location.state?.email || '';
  const userData = location.state?.userData || {};
  const isFromSignup = location.state?.signupAttempt || false;
  const isFromLogin = location.state?.userData?.resend || false;

  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('auth-page');
    document.body.classList.add('auth-page');
    return () => {
      document.documentElement.classList.remove('auth-page');
      document.body.classList.remove('auth-page');
    };
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (resendSuccess) {
      const timer = setTimeout(() => setResendSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [resendSuccess]);

  useEffect(() => {
    if (!userEmail) {
      navigate('/signup');
    }
  }, [userEmail, navigate]);

  const handleResendEmail = async () => {
    if (!canResend || resendLoading) return;

    setResendLoading(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const { error } = await signUp(userEmail, userData.tempPassword || 'temp123', userData);

      if (error) {
        if (error.message.includes('already registered') || error.message.includes('already exists')) {
          setResendSuccess(true);
          setTimeLeft(60);
          setCanResend(false);
        } else {
          setResendError(error.message);
        }
      } else {
        setResendSuccess(true);
        setTimeLeft(60);
        setCanResend(false);
      }
    } catch (err) {
      setResendError('Failed to resend email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const maskEmail = (email) => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) return email;
    const maskedLocal = localPart.charAt(0) + '*'.repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1);
    return `${maskedLocal}@${domain}`;
  };

  const handleBackNavigation = () => {
    if (isFromLogin) {
      navigate('/login');
    } else {
      navigate('/signup');
    }
  };

  const handleTryDifferentEmail = () => {
    navigate('/signup', {
      state: {
        prefillEmail: '',
        fromEmailConfirmation: true
      }
    });
  };

  const handleGoToSignIn = () => {
    navigate('/login', {
      state: {
        prefillEmail: userEmail,
        fromEmailConfirmation: true
      }
    });
  };

  if (!userEmail) {
    return null;
  }

  const steps = [
    'Check your email inbox (and spam folder)',
    'Click the confirmation link in the email',
    isFromSignup
      ? 'Start scheduling golf rounds with your friends!'
      : 'Return to sign in and access your account!'
  ];

  return (
    <div className="min-h-screen bg-app flex items-center justify-center p-4 safe-area-inset-top">
      <Helmet>
        <title>Verify Email - TeeSync</title>
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-md w-full"
      >
        {(isFromLogin || isFromSignup) && (
          <div className="mb-6">
            <button
              onClick={handleBackNavigation}
              className="flex items-center text-accent hover:underline transition-colors duration-200 font-medium text-sm"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
              {isFromLogin ? 'Back to Sign In' : 'Back to Sign Up'}
            </button>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiMail} className="w-9 h-9 text-accent" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Check your email
          </h1>
          <p className="text-text-secondary text-sm">
            We've sent a verification link to{' '}
            <span className="font-medium text-text-primary">{maskEmail(userEmail)}</span>.
            Tap it to finish setting up your account.
          </p>
        </div>

        <div className="bg-surface rounded-lg border border-border-subtle shadow-card p-6">
          <div className="flex items-center gap-3 pb-5 border-b border-border-subtle">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiMail} className="w-5 h-5 text-accent" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-text-secondary mb-0.5">
                {isFromSignup ? 'Confirmation sent to' : 'Verification sent to'}
              </p>
              <p className="text-sm font-medium text-text-primary font-mono truncate">
                {maskEmail(userEmail)}
              </p>
            </div>
          </div>

          <div className="py-5 border-b border-border-subtle">
            <h3 className="text-sm font-medium text-text-secondary mb-4">What to do next</h3>
            <ol className="space-y-3">
              {steps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 mt-0.5">
                    {index + 1}
                  </div>
                  <p className="text-sm text-text-primary pt-0.5">{step}</p>
                </li>
              ))}
            </ol>
          </div>

          {resendSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 bg-status-success/10 border border-status-success/30 text-status-success px-4 py-3 rounded-md text-sm flex items-center"
            >
              <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>
                {isFromLogin ? 'Verification email resent successfully' : 'Confirmation email resent successfully'}
              </span>
            </motion.div>
          )}

          {resendError && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-md text-sm"
            >
              {resendError}
            </motion.div>
          )}

          <div className="pt-5 space-y-3">
            {!canResend && (
              <p className="text-text-secondary text-xs flex items-center justify-center">
                <SafeIcon icon={FiClock} className="w-3.5 h-3.5 mr-1.5" />
                Resend available in {timeLeft}s
              </p>
            )}

            <button
              onClick={handleResendEmail}
              disabled={!canResend || resendLoading}
              className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
            >
              <SafeIcon
                icon={resendLoading ? FiLoader : FiRefreshCw}
                className={`w-4 h-4 mr-2 ${resendLoading ? 'animate-spin' : ''}`}
              />
              {resendLoading ? 'Sending...' : 'Resend Email'}
            </button>

            {isFromSignup && (
              <button
                onClick={handleGoToSignIn}
                className="w-full flex items-center justify-center px-4 py-3 border border-border-subtle text-text-primary rounded-md hover:bg-app transition-colors font-medium"
              >
                <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
                I Already Have an Account
              </button>
            )}

            <button
              onClick={handleTryDifferentEmail}
              className="w-full flex items-center justify-center px-4 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-app transition-colors font-medium"
            >
              <SafeIcon icon={FiEdit3} className="w-4 h-4 mr-2" />
              Use Different Email
            </button>

            {!isFromSignup && (
              <Link to="/login" className="block">
                <button className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-medium">
                  Continue to Sign In
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </button>
              </Link>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-text-secondary text-sm">
          Need help?{' '}
          <a
            href="mailto:support@teesync.app"
            className="text-accent hover:underline font-medium"
          >
            Contact Support
          </a>
        </p>
      </motion.div>
    </div>
  );
}

export default EmailConfirmation;
